import { FC, useEffect, useRef, useState } from 'react';
import { Box, Link, Text, VStack } from '@chakra-ui/react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { checkoutSubscriptionEffect } from 'app/store/Subscription/effects';
import { ContentContainerType } from 'types/enums';
import { openURL } from 'app/utils/utils';
import { OverlayHeader } from 'app/components/Dialogs';
import { PricingInterval, PricingPlan, Tier } from 'types/subscription.types';
import {
  getCurrencyList,
  getDefaultCurrency,
} from 'app/store/Subscription/selectors';
import { subscriptionTierSelectedAction } from 'app/store/Mixpanel/slice';
import { Option } from 'types/general.types';
import { ChakraSelect, ContentContainer } from 'app/components/Core';
import { externalLinks } from 'app/api.config';
import { RadioButtonGroup } from './RadioButtonGroup';
import { SubscriptionPriceCard } from './SubscriptionPriceCard';
import { paymentIntervalOptions } from '../constants';
import { GroupedTiers } from '../types';
import {
  DEFAULT_OPTION,
  formatOption,
  processCurrencyList,
  processTiers,
} from '../utils';

export interface SubscriptionTiersOverlayProps {
  tiers?: Tier[];
  onClose: () => void;
}

export const SubscriptionTiersOverlay: FC<SubscriptionTiersOverlayProps> = ({
  tiers,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const allCurrencies = useAppSelector(getCurrencyList);
  const proposedCurrency = useAppSelector(getDefaultCurrency);

  const [pricingInterval, setPricingInterval] = useState<PricingInterval>(
    'month'
  );
  const loading = useRef<boolean>(false);

  const [groupedTiers, setGroupedTiers] = useState<GroupedTiers[]>([]);
  const [currencyList, setCurrencyList] = useState<Option[]>([]);
  const [currency, setCurrency] = useState<Option>(
    (formatOption(proposedCurrency as string) as Option) || DEFAULT_OPTION
  );

  const subscribeToPremium = async (priceId: string) => {
    if (loading.current) {
      return;
    }

    const payload = {
      priceId,
      currency: currency.value as string,
    };

    loading.current = true;
    const { response } = await dispatch(
      checkoutSubscriptionEffect(payload)
    ).unwrap();
    loading.current = false;

    if (response) {
      dispatch(subscriptionTierSelectedAction({ ...payload, pricingInterval }));
      openURL(response._links.checkout.href, {
        isExternal: true,
      });
      onClose();
    }
  };

  const loadCards = (groupedTiers: GroupedTiers[]) => {
    return groupedTiers.map((group, index) => {
      const data = group[pricingInterval];

      if (data && data.length === 1) {
        return (
          <SubscriptionPriceCard
            title={group.tier.title}
            description={group.tier.description}
            // eslint-disable-next-line react/no-array-index-key
            key={`${data[0].id}--${index}`}
            pricing={data[0]}
            onClick={subscribeToPremium}
          />
        );
      }

      const filterByCurency = data?.filter((item: PricingPlan) => {
        return item.currency === currency.value;
      });

      return filterByCurency?.map((item: PricingPlan) => {
        return (
          <SubscriptionPriceCard
            title={group.tier.title}
            description={group.tier.description}
            key={`${item.id}--${item.price}`}
            pricing={item}
            onClick={subscribeToPremium}
          />
        );
      });
    });
  };

  useEffect(() => {
    if (tiers) {
      setGroupedTiers(processTiers(tiers));
    }
  }, [tiers]);

  useEffect(() => {
    if (allCurrencies) {
      setCurrencyList(processCurrencyList(allCurrencies));
    }
  }, [allCurrencies]);

  return (
    <ContentContainer type={ContentContainerType.LIST} height="100%">
      <OverlayHeader
        headerText="Select your Subscription"
        subheaderText="Select the subscription that's right for you"
        mb={4}
      />
      <VStack pb={1}>
        <Box pb={12}>
          <RadioButtonGroup
            data-testid="subscription-interval-selection"
            options={paymentIntervalOptions}
            value={pricingInterval}
            onChange={(val: string) =>
              setPricingInterval(val as PricingInterval)
            }
          />
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          flexWrap="wrap"
          justifyContent="center"
          style={{ margin: 0 }}
        >
          {loadCards(groupedTiers)}
        </Box>
      </VStack>
      <Box w={64} alignSelf="center" pt={12}>
        <ChakraSelect
          label="Currency"
          options={currencyList}
          value={currency}
          onChange={(option) => {
            if (!option) return;
            setCurrency(option);
          }}
        />
      </Box>
      <Box margin="auto">
        <Text
          maxWidth="460px"
          fontSize="xs"
          color="M.60"
          pb={2}
          textAlign="center"
        >
          GBP and EUR prices are inclusive of tax. USD prices are exclusive of
          tax.
          <br />
          &nbsp;Local tax rates will be calculated and applied at check out.
        </Text>
        <Text
          maxWidth="430px"
          fontSize="xs"
          color="M.60"
          pt={1}
          textAlign="center"
        >
          By proceeding to checkout, and subscribing to a premium tier
          <br />
          &nbsp;(subscription), you acknowledge and accept the&nbsp;
          <Link
            fontWeight={400}
            href={externalLinks.subscriptions.TERMS_AND_CONDITIONS}
            textDecoration="underline"
            target="_blank"
          >
            Terms of use
          </Link>
        </Text>
      </Box>
    </ContentContainer>
  );
};
